import * as React from "react";
import { Alert, AlertTitle, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppVersion, useMe } from "@/api/hooks";
import { reloadForServerBuild } from "@/api/loadedBuild";
import { serverStatusFrom } from "@/api/serverStatus";
import { useBrowserOnline } from "@/hooks/useBrowserPresence";
import { formatTime } from "@/i18n/format";
import { maintenanceNote } from "@/lib/maintenanceNote";
import tokens from "@/theme/tokens";

/** Sticky banner at the top of the app shell.
 *
 *  * Maintenance on: the operator's message. Everyone sees it, including
 *    the admin who switched it on (so they don't forget it after their
 *    deploy).
 *  * Backend unreachable (a deploy restarting it): "Repoworks is
 *    restarting — reconnecting…", under the last known maintenance
 *    message when there was one. "You're offline" instead when the
 *    browser knows it has no network — that is not a restart.
 *  * The server runs another build than this tab (after a deploy, or the
 *    rollback of one): "Repoworks has been updated" with a Reload button,
 *    under the maintenance message when there is one. Not while the
 *    backend is away — a reload would only reach the edge's holding page.
 *
 *  The state comes from the polled ``/api/version`` (see
 *  ``useServerStatusPoller``); ``/auth/me`` is the fallback for a backend
 *  that does not publish it there, and the only source of who set it. */
export function MaintenanceBanner(): React.JSX.Element | null {
  const { t } = useTranslation();
  const me = useMe();
  const version = useAppVersion();
  const { maintenance: polled, unreachable, updateAvailable } = serverStatusFrom(version);
  const fromMe = me.data?.maintenance;
  const mm = polled ?? fromMe ?? null;
  const active = Boolean(mm?.enabled);
  // Offline, the poll pauses rather than fails: say so without waiting.
  const offline = !useBrowserOnline();
  const away = unreachable || offline;
  const awayTitle = offline ? t("maintenance.offline_title") : t("maintenance.restarting_title");
  const offerReload = updateAvailable && !away;
  if (!active && !away && !offerReload) return null;
  const setBy = active && fromMe?.enabled ? fromMe : null;
  const reloadButton = offerReload ? (
    // The app's white CTA pill, not the org's primary: the banner is
    // Repoworks chrome, and white reads on every notice tint.
    <Button
      variant="contained"
      size="small"
      onClick={() => reloadForServerBuild(version.data?.commit_sha)}
      sx={{
        bgcolor: tokens.colors.primary,
        color: tokens.colors.primaryText,
        whiteSpace: "nowrap",
        "&:hover": { bgcolor: "#E6E6E6" },
      }}
    >
      {t("maintenance.reload")}
    </Button>
  ) : undefined;

  if (!active && offerReload) {
    return (
      <Alert
        severity="info"
        variant="filled"
        role="status"
        action={reloadButton}
        sx={{ mb: 2, position: "sticky", top: 0, zIndex: 5 }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>{t("maintenance.updated_title")}</AlertTitle>
        <Typography variant="body2">{t("maintenance.updated_body")}</Typography>
      </Alert>
    );
  }

  if (!active) {
    return (
      <Alert
        severity="info"
        variant="filled"
        role="status"
        icon={<CircularProgress size={20} color="inherit" aria-hidden />}
        sx={{ mb: 2, position: "sticky", top: 0, zIndex: 5 }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>{awayTitle}</AlertTitle>
        <Typography variant="body2">
          {offline ? t("maintenance.offline_body") : t("maintenance.restarting_body")}
        </Typography>
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      variant="filled"
      action={reloadButton}
      sx={{ mb: 2, position: "sticky", top: 0, zIndex: 5 }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {t("maintenance.title", "Maintenance mode")}
      </AlertTitle>
      {mm?.message ? (
        <Typography variant="body2">{maintenanceNote(t, mm.message)}</Typography>
      ) : (
        <Typography variant="body2">
          {t(
            "maintenance.default_body",
            "An administrator has paused Repoworks for maintenance. Your next message or run will be rejected until this notice clears.",
          )}
        </Typography>
      )}
      {away && (
        <Stack role="status" direction="row" sx={{ alignItems: "center", gap: 1, mt: 0.75 }}>
          <CircularProgress size={14} color="inherit" aria-hidden />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {awayTitle}
          </Typography>
        </Stack>
      )}
      {offerReload && (
        <Typography role="status" variant="body2" sx={{ fontWeight: 600, mt: 0.75 }}>
          {t("maintenance.updated_title")}
        </Typography>
      )}
      {setBy?.enabled_by && (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.9 }}>
          {setBy.enabled_at
            ? t("maintenance.set_by_at", "Set by {{name}} at {{time}}", {
                name: setBy.enabled_by,
                time: formatTime(setBy.enabled_at),
              })
            : t("maintenance.set_by", "Set by {{name}}", {
                name: setBy.enabled_by,
              })}
        </Typography>
      )}
    </Alert>
  );
}
